import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Anexo, ComposerPayload } from "../../pages/suporte/types";
import { styless } from "../../pages/suporte/styles";
import {
  hidratarInline,
  idsInlineDoHtml,
  serializarInline,
} from "../../utils/suporte/anexosInline";
import { textoPlano } from "../../utils/suporte/sanitize";
import { ACCEPT_ANEXOS, AnexoThumbs, useAnexos } from "./anexos";
import RichEditor from "./RichEditor";

interface ComposerProps {
  placeholder: string;
  onPreview: (url: string) => void;
  valorInicial?: string;
  anexosPublicados?: Anexo[];
  disabled?: boolean;
  dica?: string;
  dock?: boolean;
  autoFoco?: boolean;
  limparAoEnviar?: boolean;
  textoBotao?: string;
  iconeBotao?: ReactNode;
  onEnviar?: (payload: ComposerPayload) => Promise<void>;
  onCancelar?: () => void;
  onChange?: (payload: ComposerPayload) => void;
}

export default function Composer({
  placeholder,
  onPreview,
  valorInicial = "",
  anexosPublicados,
  disabled,
  dica,
  dock,
  autoFoco,
  limparAoEnviar,
  textoBotao = "Enviar",
  iconeBotao,
  onEnviar,
  onCancelar,
  onChange,
}: ComposerProps) {
  const theme = useTheme();
  const styles = styless(theme);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inserirImagensRef = useRef<((urls: string[]) => void) | null>(null);
  const publicados = useMemo(() => anexosPublicados ?? [], [anexosPublicados]);

  const anexos = useAnexos((novos) =>
    inserirImagensRef.current?.(
      novos.filter((anexo) => anexo.tipo === "imagem").map((anexo) => anexo.url),
    ),
  );

  const [texto, setTexto] = useState(() =>
    hidratarInline(valorInicial, publicados),
  );
  const [mantidos, setMantidos] = useState<Anexo[]>(() => {
    const inline = new Set(idsInlineDoHtml(valorInicial));
    return publicados.filter((anexo) => !inline.has(anexo.id));
  });
  const [enviando, setEnviando] = useState(false);
  const [ativo, setAtivo] = useState(!dock || Boolean(valorInicial));

  const plano = textoPlano(texto);
  const naFaixa = anexos.anexos
    .map((anexo, indice) => ({ anexo, indice }))
    .filter(({ anexo }) => anexo.tipo !== "imagem");

  const podeEnviar =
    plano.length > 0 ||
    texto.includes("<img") ||
    naFaixa.length > 0 ||
    mantidos.length > 0;

  const montarPayload = (): ComposerPayload => {
    const { html, imagens, idsMantidos } = serializarInline(
      texto,
      anexos.anexos,
      publicados,
    );
    const vazio = !plano && !imagens.length && !idsMantidos.length;
    return {
      texto: vazio ? "" : html,
      imagens,
      anexosMantidos: [...idsMantidos, ...mantidos.map((anexo) => anexo.id)],
    };
  };

  const reportarRef = useRef(onChange);
  reportarRef.current = onChange;
  useEffect(() => {
    if (!reportarRef.current) return;
    reportarRef.current(montarPayload());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto, anexos.anexos, mantidos]);

  const handleEnviar = async () => {
    if (!onEnviar || !podeEnviar || enviando) return;
    setEnviando(true);
    const ok = await onEnviar(montarPayload()).then(
      () => true,
      () => false,
    );
    setEnviando(false);
    if (ok && limparAoEnviar) {
      setTexto("");
      setMantidos([]);
      anexos.limpar();
      if (dock) setAtivo(false);
    }
  };

  const bloqueado = disabled || enviando;

  const anexarBotao = (
    <>
      <Tooltip title="Anexar imagem ou arquivo" arrow>
        <span>
          <IconButton
            size="small"
            disabled={bloqueado}
            onClick={() => {
              setAtivo(true);
              fileInputRef.current?.click();
            }}
          >
            <AttachFileIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ANEXOS}
        multiple
        hidden
        onChange={anexos.handleFilePick}
      />
    </>
  );

  return (
    <Box sx={styles.composer}>
      {ativo ? (
        <RichEditor
          value={texto}
          onChange={setTexto}
          placeholder={placeholder}
          disabled={bloqueado}
          compacto={dock}
          autoFoco={autoFoco || (dock && !valorInicial)}
          onPaste={anexos.handlePaste}
          inserirImagensRef={inserirImagensRef}
        />
      ) : (
        <TextField
          value=""
          onFocus={() => setAtivo(true)}
          placeholder={placeholder}
          disabled={bloqueado}
          fullWidth
          sx={styles.dockGatilho}
        />
      )}

      {!!mantidos.length && (
        <AnexoThumbs
          itens={mantidos}
          onPreview={onPreview}
          onRemove={(i) =>
            setMantidos((atuais) => atuais.filter((_, j) => j !== i))
          }
        />
      )}
      <AnexoThumbs
        itens={naFaixa.map(({ anexo }) => anexo)}
        onPreview={onPreview}
        onRemove={(i) => anexos.removerAnexo(naFaixa[i].indice)}
      />

      {onEnviar ? (
        <Box sx={styles.composerAcoes}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {anexarBotao}
            {dica && !dock && (
              <Typography sx={styles.composerDica}>{dica}</Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {onCancelar && (
              <Button
                size="small"
                color="inherit"
                disabled={enviando}
                onClick={onCancelar}
                sx={{ textTransform: "none" }}
              >
                Cancelar
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              disableElevation
              disabled={!podeEnviar || bloqueado}
              onClick={handleEnviar}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
              startIcon={
                enviando ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  iconeBotao
                )
              }
            >
              {textoBotao}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {anexarBotao}
          {dica && <Typography sx={styles.composerDica}>{dica}</Typography>}
        </Box>
      )}
    </Box>
  );
}
